import { CustomerTypeEnum } from 'pejer/src/tables/Global'
import { z } from 'zod'

// Grensene speiler InvoiceCustomerDetailsRequest i Pejer-Core-Server (MaxLength(64)).
// Avviker vi herfra, får kunden «One or more validation errors occurred» fra serveren.
const SERVER_MAX_LENGTH = 64

// Nettleserens autoutfylling kan legge på mellomrom (også hardt mellomrom), som
// serveren avviser. Tekstfelt trimmes, tallfelt får alle mellomrom fjernet, og
// vee-validate sender de rensede verdiene videre til onSubmit.
const removeWhitespace = (value: string) => value.replace(/\s/g, '')

export const invoiceOptionsSchema = z
  .object({
    customerType: z.enum([CustomerTypeEnum.Individual, CustomerTypeEnum.Business]),
    customerEmail: z
      .string()
      .trim()
      .min(1, {
        message: 'E-post er påkrevd',
      })
      .email({
        message: 'E-post må være en gyldig e-postadresse',
      })
      .max(SERVER_MAX_LENGTH, {
        message: `E-post må være maks ${SERVER_MAX_LENGTH} tegn`,
      }),
    // Påkrevd fordi fakturaen kan gå via Dintero (Kravia), som krever mobilnummer. Uten landskode bruker serveren +47.
    customerMobile: z
      .string({
        required_error: 'Mobilnummer er påkrevd',
      })
      .transform(removeWhitespace)
      .pipe(
        z
          .string()
          .min(1, {
            message: 'Mobilnummer er påkrevd',
          })
          .regex(/^(\+\d{8,15}|\d{8})$/, {
            message: 'Mobilnummer må være 8 siffer, eller med landskode (f.eks. +4791234567)',
          })
      ),
    customerName: z
      .string()
      .trim()
      .min(1, {
        message: 'Navn er påkrevd',
      })
      .max(SERVER_MAX_LENGTH, {
        message: `Navn må være maks ${SERVER_MAX_LENGTH} tegn`,
      }),
    customerAddress: z
      .string()
      .trim()
      .min(1, {
        message: 'Adresse er påkrevd',
      })
      .max(SERVER_MAX_LENGTH, {
        message: `Adressen må være maks ${SERVER_MAX_LENGTH} tegn`,
      }),
    customerPostalCode: z
      .string({
        required_error: 'Postnummer er påkrevd',
      })
      .transform(removeWhitespace)
      .pipe(
        z
          .string()
          .min(1, {
            message: 'Postnummer er påkrevd',
          })
          .regex(/^\d*$/, {
            message: 'Postnummer kan bare inneholde siffer',
          })
          .max(10, {
            message: 'Postnummer må være maks 10 siffer',
          })
      ),
    customerCity: z
      .string()
      .trim()
      .min(1, {
        message: 'Poststed er påkrevd',
      })
      .max(30, {
        message: 'Poststed må være maks 30 tegn',
      }),
    customerSSN: z.string().transform(removeWhitespace).optional(),
    customerOrganizationNumber: z.string().transform(removeWhitespace).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.customerType === CustomerTypeEnum.Individual) {
      if (!data.customerSSN) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customerSSN'],
          message: 'Fødselsnummer er påkrevd for privatpersoner',
        })
      } else if (!/^\d{11}$/.test(data.customerSSN)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customerSSN'],
          message: 'Fødselsnummer må være 11 siffer',
        })
      }
    } else if (data.customerType === CustomerTypeEnum.Business) {
      if (!data.customerOrganizationNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customerOrganizationNumber'],
          message: 'Organisasjonsnummer er påkrevd for bedrifter',
        })
      } else if (!/^\d{9}$/.test(data.customerOrganizationNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customerOrganizationNumber'],
          message: 'Organisasjonsnummer må være 9 siffer',
        })
      }
    }
  })

export const avtalegiroSchema = z
  .object({
    customerEmail: z
      .string()
      .nonempty({
        message: 'E-post er påkrevd',
      })
      .email({
        message: 'E-post må være en gyldig e-postadresse',
      }),
    customerName: z
      .string()
      .nonempty({
        message: 'Navn er påkrevd',
      })
      .max(100, {
        message: 'Navn må være midre enn 64 tegn',
      }),
    customerAddress: z
      .string()
      .nonempty({
        message: 'Adresse er påkrevd',
      })
      .max(255, {
        message: 'Adressen må være midre enn 255 tegn',
      }),
    customerPostalCode: z
      .string({
        required_error: 'Postnummer er påkrevd',
      })
      .nonempty({
        message: 'Postnummer er påkrevd',
      })
      .max(10, {
        message: 'Postnummer må være midre enn 10 tegn',
      }),
    customerCity: z
      .string()
      .nonempty({
        message: 'Poststed er påkrevd',
      })
      .max(30, {
        message: 'Poststed må være midre enn 30 tegn',
      }),
    customerSSN: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.customerSSN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customerSSN'],
        message: 'Fødselsnummer er påkrevd for privatpersoner',
      })
    } else if (!/^\d{11}$/.test(data.customerSSN)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customerSSN'],
        message: 'Fødselsnummer må være 11 siffer',
      })
    }
  })
